import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import apiService from "../services/apiService";

const AddProperty = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    sellerCompanyId: "", // Add seller company ID to form data
    categoryId: "",
    propertyStatusId: "",
    provinceId: "",
    countryId: "",
    agencyId: "",
    agentId: "",
    listingTitle: "",
    listingDescription: "",
    shortDescription: "",
    // Remove keywords field
    addressLine1: "",
    addressLine2: "",
    addressLine3: "",
    suburb: "",
    // Remove nearestTown from form state
    // Remove all checkbox fields: isFeatured, isListable, isDiscrete, isActive
  });

  // Dropdown data
  const [sellerCompanies, setSellerCompanies] = useState([]); // Add seller companies state
  const [categories, setCategories] = useState([]);
  const [propertyStatuses, setPropertyStatuses] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [countries, setCountries] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [agents, setAgents] = useState([]);

  // Fetch dropdown data
  useEffect(() => {
    fetchDropdownData();
  }, []);

  const fetchDropdownData = async () => {
    try {
      // Fetch all dropdown data in parallel
      const [
        sellerCompaniesData,
        categoriesData,
        statusData,
        provincesData,
        countriesData,
        agenciesData,
      ] = await Promise.all([
        apiService.get("/seller-companies"),
        apiService.get("/categories"),
        apiService.get("/property-status"),
        apiService.get("/provinces"),
        apiService.get("/countries"),
        apiService.get("/agencies"),
      ]);

      setSellerCompanies(sellerCompaniesData);
      setCategories(categoriesData);
      setPropertyStatuses(statusData);
      setProvinces(provincesData);
      setCountries(countriesData);
      setAgencies(agenciesData);
    } catch (error) {
      console.error("Error fetching dropdown data:", error);
      setError("Failed to load form data");
    }
  };

  const handleAgencyChange = async (agencyId) => {
    setFormData({ ...formData, agencyId, agentId: "" });

    if (agencyId) {
      try {
        const response = await apiService.get("/users?roleId=6");
        
        // Extract users array from response object
        const agentsData = response.users || [];
        
        // Find the selected agency
        const selectedAgency = agencies.find((a) => a.id == agencyId);
        
        if (!selectedAgency) {
          console.warn("Selected agency not found");
          setAgents([]);
          return;
        }
        
        // Filter agents by agency name - only include agents with matching agencyName
        const filteredAgents = agentsData.filter(
          (agent) => agent.agencyName === selectedAgency.agencyName
        );
        
        console.log("Fetched agents:", agentsData.length, "Filtered agents:", filteredAgents.length, "Selected agency:", selectedAgency.agencyName);
        setAgents(filteredAgents);
      } catch (error) {
        console.error("Error fetching agents:", error);
        setAgents([]);
      }
    } else {
      setAgents([]);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const result = await apiService.post("/properties", {
        userId: 3, // Hardcoded for now
        categoryId: parseInt(formData.categoryId),
        agentId: parseInt(formData.agentId),
        sellerCompanyId: parseInt(formData.sellerCompanyId), // Use selected seller company ID
        listingTitle: formData.listingTitle,
        listingDescription: formData.listingDescription,
        title: formData.listingTitle, // Use listingTitle value for title field
        shortDescription: formData.shortDescription,
        // Remove keywords field
        addressLine1: formData.addressLine1,
        addressLine2: formData.addressLine2,
        addressLine3: formData.addressLine3,
        suburb: formData.suburb,
        nearestTown: "N/A", // Default to 'N/A'
        provinceId: parseInt(formData.provinceId),
        countryId: parseInt(formData.countryId),
        propertyStatusId: parseInt(formData.propertyStatusId),
      });

      alert("Property created successfully!");
      navigate("/properties");
    } catch (error) {
      console.error("Error creating property:", error);
      setError("Failed to create property. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (error) {
    return (
      <div className="error-container">
        <p>{error}</p>
        <button onClick={() => setError(null)}>Try Again</button>
      </div>
    );
  }

  return (
    <div className="add-property-container">
      <h2>Add New Property</h2>

      <form onSubmit={handleSubmit} className="property-form">
        {/* Address Information - MOVED TO TOP */}
        <div className="form-section">
          <h3>Address Information</h3>
          <div className="form-grid">
            <div className="form-row">
              <label htmlFor="addressLine1">Address Line 1:</label>
              <input
                type="text"
                id="addressLine1"
                name="addressLine1"
                value={formData.addressLine1}
                onChange={handleInputChange}
                required
                className="form-input"
              />
            </div>

            <div className="form-row">
              <label htmlFor="addressLine2">Address Line 2:</label>
              <input
                type="text"
                id="addressLine2"
                name="addressLine2"
                value={formData.addressLine2}
                onChange={handleInputChange}
                className="form-input"
              />
            </div>

            <div className="form-row">
              <label htmlFor="addressLine3">Address Line 3:</label>
              <input
                type="text"
                id="addressLine3"
                name="addressLine3"
                value={formData.addressLine3}
                onChange={handleInputChange}
                className="form-input"
              />
            </div>

            <div className="form-row">
              <label htmlFor="suburb">Suburb:</label>
              <input
                type="text"
                id="suburb"
                name="suburb"
                value={formData.suburb}
                onChange={handleInputChange}
                required
                className="form-input"
              />
            </div>

            {/* Remove the Nearest Town field entirely */}

            <div className="form-row">
              <label htmlFor="provinceId">Province:</label>
              <select
                id="provinceId"
                name="provinceId"
                value={formData.provinceId}
                onChange={handleInputChange}
                required
                className="form-select"
              >
                <option value="">Select province...</option>
                {provinces.map((province) => (
                  <option key={province.id} value={province.id}>
                    {province.title}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-row">
              <label htmlFor="countryId">Country:</label>
              <select
                id="countryId"
                name="countryId"
                value={formData.countryId}
                onChange={handleInputChange}
                required
                className="form-select"
              >
                <option value="">Select country...</option>
                {countries.map((country) => (
                  <option key={country.id} value={country.id}>
                    {country.title}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Basic Information - MOVED TO SECOND POSITION */}
        <div className="form-section">
          <h3>Basic Information</h3>
          <div className="form-grid">
            {/* Add Seller dropdown at the very top */}
            <div className="form-row">
              <label htmlFor="sellerCompanyId">Seller: *</label>
              <select
                id="sellerCompanyId"
                name="sellerCompanyId"
                value={formData.sellerCompanyId}
                onChange={handleInputChange}
                required
                className="form-select"
              >
                <option value="">Select seller...</option>
                {sellerCompanies.map((seller) => (
                  <option key={seller.id} value={seller.id}>
                    {seller.companyName}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-row">
              <label htmlFor="listingTitle">Property Name:</label>
              <input
                type="text"
                id="listingTitle"
                name="listingTitle"
                value={formData.listingTitle}
                onChange={handleInputChange}
                required
                className="form-input"
              />
            </div>

            <div className="form-row">
              <label htmlFor="categoryId">Category:</label>
              <select
                id="categoryId"
                name="categoryId"
                value={formData.categoryId}
                onChange={handleInputChange}
                required
                className="form-select"
              >
                <option value="">Select category...</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.title}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-row">
              <label htmlFor="propertyStatusId">Property Status:</label>
              <select
                id="propertyStatusId"
                name="propertyStatusId"
                value={formData.propertyStatusId}
                onChange={handleInputChange}
                required
                className="form-select"
              >
                <option value="">Select status...</option>
                {propertyStatuses.map((status) => (
                  <option key={status.id} value={status.id}>
                    {status.title}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Agent Information */}
        <div className="form-section">
          <h3>Agent Information</h3>
          <div className="form-grid">
            <div className="form-row">
              <label htmlFor="agencyId">Agency:</label>
              <select
                id="agencyId"
                name="agencyId"
                value={formData.agencyId}
                onChange={(e) => handleAgencyChange(e.target.value)}
                required
                className="form-select"
              >
                <option value="">Select agency...</option>
                {agencies.map((agency) => (
                  <option key={agency.id} value={agency.id}>
                    {agency.agencyName}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-row">
              <label htmlFor="agentId">Agent:</label>
              <select
                id="agentId"
                name="agentId"
                value={formData.agentId}
                onChange={handleInputChange}
                required
                className="form-select"
                disabled={!formData.agencyId}
              >
                <option value="">Select agent...</option>
                {agents.map((agent) => (
                  <option key={agent.id} value={agent.id}>
                    {agent.firstName} {agent.lastName}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Descriptions */}
        <div className="form-section">
          <h3>Descriptions</h3>
          <div className="form-grid">
            <div className="form-row full-width">
              <label htmlFor="listingDescription">Teaser:</label>
              <textarea
                id="listingDescription"
                name="listingDescription"
                value={formData.listingDescription}
                onChange={handleInputChange}
                required
                className="form-textarea"
                rows="4"
              />
            </div>

            <div className="form-row full-width">
              <label htmlFor="shortDescription">Introduction:</label>
              <textarea
                id="shortDescription"
                name="shortDescription"
                value={formData.shortDescription}
                onChange={handleInputChange}
                required
                className="form-textarea"
                rows="3"
              />
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="form-actions">
          <button type="submit" disabled={loading} className="btn btn-primary">
            {loading ? "Creating..." : "Create Property"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddProperty;
